import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { modalSelector } from 'src/state/selectors/modal';
import { hideModal } from 'src/state/slices/modal';
import {
  Button,
  ButtonIcon,
  IconClose,
  IconLineArrowLeft,
  IconLineArrowRight,
  Spacer,
} from '@odekoteam/doppio';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import ClockwiseArrow from 'assets/svgs/clockwiseArrow.svg';
import CounterClockwiseArrow from 'assets/svgs/counterClockwiseArrow.svg';
import { styledFullscreenModal, exitButtonWrapper, image } from './styles';
import { useEffect, useRef, useState } from 'react';
import { Flex } from 'theme-ui';

const FullscreenModal = (): JSX.Element | null => {
  const { variables = {} } = useAppSelector(modalSelector);
  const { index = 0, attachments = [], images } = variables;
  const viewerImages = images ?? attachments;
  const dispatch = useAppDispatch();
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [viewingIndex, setViewingIndex] = useState(index);
  const ref = useRef<HTMLDivElement>(null);
  const [rotationIndex, setRotationIndex] = useState(0);
  const rotationDegrees = [0, 90, 180, 270];
  const currentImage = viewerImages[viewingIndex];
  const isNextDisabled = viewingIndex === viewerImages.length - 1;
  const isPreviousDisabled = viewingIndex === 0;
  const arrowStyles = { alignSelf: 'center', marginX: '10px' };

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.clientHeight - 80);
      setWidth(ref.current.clientWidth - 150);
    }
  }, []);

  const handleOnClickCancel = (): void => {
    dispatch(hideModal());
  };

  const handleOnNext = (): void => {
    setViewingIndex(viewingIndex + 1);
    setRotationIndex(0);
  };

  const handleOnPrevious = (): void => {
    setViewingIndex(viewingIndex - 1);
    setRotationIndex(0);
  };

  const handleRotateClockwise = (): void => {
    if (rotationIndex === 3) setRotationIndex(0);
    else setRotationIndex(rotationIndex + 1);
  };

  const handleRotateCounterClockwise = (): void => {
    if (rotationIndex === 0) setRotationIndex(3);
    else setRotationIndex(rotationIndex - 1);
  };

  if (!currentImage) return null;

  return (
    <div id="fullscreen-modal" sx={styledFullscreenModal} ref={ref}>
      <button
        type="button"
        sx={exitButtonWrapper}
        onClick={handleOnClickCancel}
        id="fullscreen-modal-dismiss-button"
        aria-label="Close full-screen viewer"
      >
        <IconClose width={24} height={24} />
      </button>
      <div sx={{ padding: '20px' }}>
        <TransformWrapper>
          {({ zoomIn, zoomOut, resetTransform }): JSX.Element => (
            <Flex>
              <span
                sx={{
                  ...arrowStyles,
                  visibility: isPreviousDisabled ? 'hidden' : 'inherit',
                }}
              >
                <ButtonIcon
                  variant="ghost"
                  accessibilityLabel="Previous image"
                  onPress={(): void => {
                    handleOnPrevious();
                    resetTransform();
                  }}
                >
                  <IconLineArrowLeft />
                </ButtonIcon>
              </span>
              <div>
                <Flex className="tools" sx={{ marginY: '10px' }}>
                  <ButtonIcon
                    variant="tertiary"
                    accessibilityLabel="Rotate image counterclockwise"
                    onPress={handleRotateCounterClockwise}
                  >
                    <Flex>
                      <CounterClockwiseArrow />
                    </Flex>
                  </ButtonIcon>
                  <Spacer width="$1" />
                  <ButtonIcon
                    variant="tertiary"
                    accessibilityLabel="Rotate image clockwise"
                    onPress={handleRotateClockwise}
                  >
                    <Flex>
                      <ClockwiseArrow />
                    </Flex>
                  </ButtonIcon>
                  <Spacer width="$1" />
                  <Button variant="tertiary" onPress={(): void => zoomIn()}>
                    Zoom in +
                  </Button>
                  <Spacer width="$1" />
                  <Button variant="tertiary" onPress={(): void => zoomOut()}>
                    Zoom out -
                  </Button>
                  <Spacer width="$1" />
                  <Button
                    variant="secondary"
                    onPress={(): void => resetTransform()}
                  >
                    Reset zoom
                  </Button>
                </Flex>
                <TransformComponent contentStyle={{ height, width }}>
                  <img
                    src={currentImage.url}
                    alt={
                      currentImage.alt ??
                      `Image ${viewingIndex + 1} of ${viewerImages.length}`
                    }
                    sx={{
                      ...image,
                      transform: `rotate(${rotationDegrees[rotationIndex]}deg)`,
                    }}
                  />
                </TransformComponent>
              </div>
              <span
                sx={{
                  ...arrowStyles,
                  visibility: isNextDisabled ? 'hidden' : 'inherit',
                }}
              >
                <ButtonIcon
                  variant="ghost"
                  accessibilityLabel="Next image"
                  onPress={(): void => {
                    handleOnNext();
                    resetTransform();
                  }}
                >
                  <IconLineArrowRight />
                </ButtonIcon>
              </span>
            </Flex>
          )}
        </TransformWrapper>
      </div>
    </div>
  );
};
export default FullscreenModal;
